// ============================================================================
// Activos — formato de fechas, montos y errores de captura (#1638)
// ============================================================================
// Archivo común, idéntico en los dos forks. Los textos de las validaciones
// salen de `VALIDATION_CODE_LABELS` de `@torre/assets`: el mismo código que
// devuelve el API (`{ field, code }`) es el que se pinta junto al campo.
// ============================================================================
import { BASE_CURRENCY, VALIDATION_CODE_LABELS, isIsoDate, isoDateInTimeZone, type ValidationIssue } from "@torre/assets";
import type { Lang } from "./vocab";

const LOCALE: Record<Lang, string> = { es: "es-MX", en: "en-US" };

/** `YYYY-MM-DD` legible, sin correrse de día por la zona horaria. */
export function formatDate(value: string | null | undefined, lang: Lang): string {
  if (!value || !isIsoDate(value)) return "—";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(LOCALE[lang], { day: "2-digit", month: "short", year: "numeric" });
}

/** Sello ISO completo (auditoría, eventos) con hora. */
export function formatDateTime(value: string | null | undefined, lang: Lang): string {
  if (!value) return "—";
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleString(LOCALE[lang], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Monto con su moneda; `null` es «—» (sin costo o sin `feat:activos.costos`). */
export function formatMoney(amount: number | null | undefined, currency: string | null | undefined, lang: Lang): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const cur = (currency || BASE_CURRENCY).toUpperCase();
  try {
    return new Intl.NumberFormat(LOCALE[lang], { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${cur}`;
  }
}

/** Texto de un campo numérico → número, o `null` si está vacío. Lo inválido queda como NaN y lo rechaza la validación. */
export function parseAmount(value: string): number | null {
  const v = value.trim().replace(/,/g, "");
  if (!v) return null;
  return Number(v);
}

/** Entero opcional (vida útil en meses). */
export function parseInteger(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  return Number(v);
}

/** Texto opcional: vacío → null. */
export const optionalText = (value: string): string | null => (value.trim() ? value.trim() : null);

/** `{ campo: mensaje }` a partir de los errores de la validación (o del 422 del API). */
export function fieldErrors(issues: readonly ValidationIssue[] | null | undefined, lang: Lang): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues || []) {
    const key = issue.field || "_";
    if (out[key]) continue;
    const label = VALIDATION_CODE_LABELS[issue.code];
    out[key] = label ? label[lang] : issue.code;
  }
  return out;
}

/** Hoy en `YYYY-MM-DD`, en la zona de la plataforma (la misma que usa el API). */
export const todayIso = (at: Date = new Date()): string => isoDateInTimeZone(at);
